
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, RefreshCw, AlertTriangle, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Props {
  children: ReactNode;
  componentName?: string;
  showNavigation?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class AdminErrorBoundaryClass extends Component<Props & { navigate?: (path: string) => void }, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`Admin Error Boundary (${this.props.componentName || 'Unknown'}):`, error, errorInfo);
    
    this.setState({ errorInfo });
    
    // Log to external service if needed
    // logErrorToService(error, errorInfo, this.props.componentName);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private handleGoHome = () => {
    if (this.props.navigate) {
      this.props.navigate('/admin');
    }
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Admin Component Error:</strong> {this.props.componentName || 'Unknown component'} has encountered an error.
            </AlertDescription>
          </Alert>
          
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-800">
                <Shield className="h-5 w-5" />
                Admin Dashboard Error
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-red-700">
                The {this.props.componentName || 'admin component'} encountered an unexpected error. 
                This has been logged for investigation.
              </p>
              
              {this.state.error && (
                <details className="text-sm">
                  <summary className="cursor-pointer font-medium text-red-600">
                    Technical details
                  </summary>
                  <div className="mt-2 p-3 bg-red-50 rounded border">
                    <p><strong>Error:</strong> {this.state.error.message}</p>
                    {this.state.errorInfo && (
                      <pre className="mt-2 text-xs whitespace-pre-wrap break-words text-red-600">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                </details>
              )}
              
              <div className="flex gap-2">
                <Button onClick={this.handleRetry} variant="outline" className="flex-1">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry Component
                </Button>
                
                {this.props.showNavigation && (
                  <Button onClick={this.handleGoHome} variant="secondary" className="flex-1">
                    <Home className="h-4 w-4 mr-2" />
                    Back to Dashboard
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrapper component to inject navigate
const AdminErrorBoundary: React.FC<Props> = ({ children, componentName, showNavigation = true }) => {
  const navigate = useNavigate();
  
  return (
    <AdminErrorBoundaryClass 
      componentName={componentName}
      showNavigation={showNavigation}
      navigate={navigate}
    >
      {children}
    </AdminErrorBoundaryClass>
  );
};

export default AdminErrorBoundary;
